import { createCheckout, getCheckout } from '@wix/ambassador-ecom-v1-checkout/http';
import { ChannelType, Checkout } from '@wix/ambassador-ecom-v1-checkout/types';
import { addLineItems, createCart, getCart } from '@wix/ambassador-ecom-v2-cart/http';
import { type Cart, type CatalogItemInput, ChannelType as ChannelTypeV2 } from '@wix/ambassador-ecom-v2-cart/types';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import type { AdditionalLineItem } from '@wix/pricing-plans-router-utils';
import type { ControllerFlowAPI, ViewerScriptFlowAPI } from '@wix/yoshi-flow-editor';
import { PRICING_PLANS_APP_DEF_ID } from '../constants';
import type { PlanOptions } from '../types/ecom';
import { EcomServerless } from './ecom-serverless';

export class EcomService {
  constructor(private readonly flowAPI: ControllerFlowAPI | ViewerScriptFlowAPI) {}

  async createCheckout(
    planId: string,
    planOptions: PlanOptions,
    checkoutFlowId: string | undefined,
  ): Promise<Checkout> {
    const {
      httpClient,
      errorHandler: { withErrorHandler },
      translations: { t },
    } = this.flowAPI;

    const serverless = new EcomServerless(this.flowAPI.httpClient, this.flowAPI.errorHandler);
    const address = this.flowAPI.experiments.enabled(TPA_EXPERIMENTS.ECOM_GEOIP_ADDRESS)
      ? await serverless.getAddress()
      : null;

    const result = await withErrorHandler(
      () =>
        httpClient.request(
          createCheckout({
            checkoutInfo: {
              externalReference: {
                appId: PRICING_PLANS_APP_DEF_ID,
                resourceId: checkoutFlowId,
              },
              billingInfo: address ? { address } : undefined,
            },
            channelType: ChannelType.WEB,
            lineItems: [
              {
                quantity: 1,
                catalogReference: {
                  appId: PRICING_PLANS_APP_DEF_ID,
                  catalogItemId: planId,
                  options: {
                    type: 'PLAN',
                    planOptions,
                  },
                },
              },
            ],
          }),
        ),
      {
        errorCodesMap: {
          applicationError: {
            SITE_MUST_ACCEPT_PAYMENTS: () => ({
              message: t('ecom-v1-checkout.application-error.site-must-accept-payments'),
            }),
            SITE_MUST_ACCEPT_PAYMENTS_TO_CREATE_CHECKOUT: () => ({
              message: t('ecom-v1-checkout.application-error.site-must-accept-payments'),
            }),

            // Coupon, Gift card, or carrier service errors are not relevant for our use case
            SELECTED_CARRIER_SERVICE_OPTION_NOT_FOUND: false,
            INVALID_SELECTED_CARRIER_SERVICE_OPTION: false,
            GIFT_CARD_NOT_FOUND: false,
            GIFT_CARD_CODE_INVALID: false,
            GIFT_CARD_BALANCE_TOO_LOW: false,
            GIFT_CARD_EXPIRED: false,
            GIFT_CARD_NOT_ACTIVE: false,
            GIFT_CARD_ALREADY_USED: false,
            GIFT_CARD_CURRENCY_MISMATCH: false,
            GIFT_CARD_AMOUNT_TOO_HIGH: false,
            GIFT_CARD_AMOUNT_TOO_LOW: false,
            GIFT_CARD_IS_EMPTY: false,
            GIFT_CARD_DISABLED: false,
            ERROR_COUPON_USAGE_EXCEEDED: false,
            ERROR_COUPON_LIMIT_PER_CUSTOMER_EXCEEDED: false,
            ERROR_COUPON_DOES_NOT_EXIST: false,
            ERROR_COUPON_IS_NOT_ACTIVE_YET: false,
            ERROR_COUPON_HAS_EXPIRED: false,
          },
        },
      },
    );
    return result.data.checkout!;
  }

  async getCheckout(checkoutId: string): Promise<Checkout> {
    const { httpClient, errorHandler } = this.flowAPI;

    const result = await errorHandler.withErrorHandler(() => httpClient.request(getCheckout({ id: checkoutId })), {
      errorCodesMap: {
        applicationError: {},
      },
    });
    return result.data.checkout!;
  }

  async createCartV2(
    planId: string,
    planOptions: PlanOptions,
    checkoutFlowId: string | undefined,
    additionalLineItems?: AdditionalLineItem[],
  ): Promise<Cart> {
    const {
      httpClient,
      errorHandler: { withErrorHandler },
      translations: { t },
    } = this.flowAPI;

    const serverless = new EcomServerless(this.flowAPI.httpClient, this.flowAPI.errorHandler);
    const address = this.flowAPI.experiments.enabled(TPA_EXPERIMENTS.ECOM_GEOIP_ADDRESS)
      ? await serverless.getAddress()
      : null;

    const planCatalogItem: CatalogItemInput = {
      quantity: 1,
      catalogReference: {
        appId: PRICING_PLANS_APP_DEF_ID,
        catalogItemId: planId,
        options: {
          type: 'PLAN',
          planOptions,
        },
      },
    };

    const extraItems = additionalLineItems ?? [];
    const requiresFutureMembership = extraItems.some((item) => item.membershipPayment?.futureMembership);

    const result = await withErrorHandler(
      () =>
        httpClient.request(
          createCart({
            cart: {
              source: {
                channelType: ChannelTypeV2.WEB,
                externalReferences: [
                  {
                    appId: PRICING_PLANS_APP_DEF_ID,
                    resourceId: checkoutFlowId,
                  },
                ],
              },
              paymentInfo: address ? { billingAddress: address } : undefined,
            },
            catalogItems: requiresFutureMembership ? [planCatalogItem] : [planCatalogItem, ...extraItems],
          }),
        ),
      {
        errorCodesMap: {
          applicationError: {
            SITE_MUST_ACCEPT_PAYMENTS: () => ({
              message: t('ecom-v1-checkout.application-error.site-must-accept-payments'),
            }),
            // Not relevant for our use case
            ITEM_NOT_FOUND_IN_CATALOG: false,
            INSUFFICIENT_INVENTORY: false,
            MAX_LINE_ITEMS_EXCEEDED: false,
            MAX_ITEM_MODIFIERS_EXCEEDED: false,
            DUPLICATE_MODIFIER_GROUP_IDS: false,
            DUPLICATE_ITEM_MODIFIER_IDS: false,
            LINE_ITEM_PRICE_CONFLICT: false,
            FIXED_QUANTITY_ITEM: false,
            INVALID_MEMBERSHIP_PAYMENT_OPTION: false,
            INVALID_COUPON_CODE: false,
            INVALID_COUPON_STATUS: false,
            INVALID_GIFT_CARD_CODE: false,
            INVALID_GIFT_CARD_STATUS: false,
            DELIVERY_METHOD_NOT_FOUND: false,
            DELIVERY_METHOD_MISSING_ADDRESS: false,
            DELIVERY_METHOD_MISSING_LINE_ITEMS: false,
            DEPOSIT_AMOUNT_GREATER_THAN_UNIT_PRICE: false,
          },
        },
      },
    );
    const cart = result.data.cart!;

    return requiresFutureMembership ? this.addItemsCoveredByPlan(cart, planId, extraItems) : cart;
  }

  private async addItemsCoveredByPlan(cart: Cart, planId: string, items: AdditionalLineItem[]): Promise<Cart> {
    const { httpClient } = this.flowAPI;

    const planLineItemId = cart.lineItems?.find(
      (lineItem) =>
        lineItem.source?.catalogReference?.appId === PRICING_PLANS_APP_DEF_ID &&
        lineItem.source?.catalogReference?.catalogItemId === planId,
    )?.id;

    const catalogItems: CatalogItemInput[] = items.map((item) =>
      item.membershipPayment?.futureMembership
        ? {
            ...item,
            membershipPayment: {
              ...item.membershipPayment,
              futureMembership: { ...item.membershipPayment.futureMembership, lineItemId: planLineItemId },
            },
          }
        : item,
    );

    const result = await this.flowAPI.errorHandler.withErrorHandler(
      () => httpClient.request(addLineItems({ cartId: cart.id!, catalogItems })),
      { errorCodesMap: { applicationError: {} } },
    );
    return result.data.cart!;
  }

  async getCartById(cartId: string): Promise<Cart> {
    const { httpClient, errorHandler } = this.flowAPI;

    const result = await errorHandler.withErrorHandler(() => httpClient.request(getCart({ cartId })), {
      errorCodesMap: {
        applicationError: {},
      },
    });
    return result.data.cart!;
  }
}
